import Helpers from "./helper/Helpers.js";
import Objects from "./helper/util/Objects.js";
const { defineProperty, getOwnPropertyDescriptor } = Reflect, { hasOwnProperty, toString } = Object.prototype, instancePropertiesSymbol = Symbol("instanceProperties"), configuringSymbol = Symbol("configuring"), lazyConfigValues = Symbol("lazyConfigValues"), DATE_TYPE = toString.call(/* @__PURE__ */ new Date()), whitespace = /\s+/, hasOwn = Object.hasOwn || ((object, prop) => hasOwnProperty.call(object, prop)), dateEquals = (value1, value2) => {
  if (value1 === value2) {
    return true;
  }
  if (value1 && value2 && toString.call(value1) === DATE_TYPE && toString.call(value2) === DATE_TYPE) {
    return value1.getTime() === value2.getTime();
  }
  return false;
}, getDay = (d, offset) => {
  d.setHours(0, 0, 0, 0);
  offset && d.setDate(d.getDate() + offset);
  return d;
}, specialDates = {
  now: () => /* @__PURE__ */ new Date(),
  today: () => getDay(/* @__PURE__ */ new Date()),
  tomorrow: () => getDay(/* @__PURE__ */ new Date(), 1),
  yesterday: () => getDay(/* @__PURE__ */ new Date(), -1)
}, createClsProps = (result, cls) => {
  result[cls] = 1;
  return result;
}, observableGet = (name, get) => function() {
  var _a;
  (_a = this.configObserver) == null ? void 0 : _a.get(name, this);
  return get.call(this);
};
class Config {
  /**
   * Returns the `Config` instance for the given `name` and `options`.
   * @param {String} name The name of the config (e.g., 'text' for the text config).
   * @param {Object} [options] Config behavior options.
   * @returns {Core.Config}
   * @internal
   */
  static get(name, options) {
    const { cache } = this, baseCfg = cache[name] || (cache[name] = new Config(name));
    let cfg = baseCfg, key;
    if (options) {
      key = Config.makeCacheKey(name, options);
      if (!(cfg = key && cache[key])) {
        cfg = baseCfg.extend(options);
        if (key) {
          cache[key] = cfg;
        }
      }
    }
    return cfg;
  }
  constructor(name) {
    const me = this, cap = name[0].toUpperCase() + name.substr(1);
    me.base = me;
    me.name = name;
    me.field = "_" + name;
    me.capName = cap;
    me.changer = "change" + cap;
    me.initializing = "initializing" + cap;
    me.updater = "update" + cap;
  }
  /**
   * The descriptor to use with `Reflect.defineProperty()` for defining this config's getter and setter.
   * @property {Object}
   * @private
   */
  get descriptor() {
    let descriptor = this._descriptor;
    if (!descriptor || !hasOwn(this, "_descriptor")) {
      this._descriptor = descriptor = this.makeDescriptor();
    }
    return descriptor;
  }
  /**
   * The descriptor to use with `Reflect.defineProperty()` for defining this config's initter.
   * @property {Object}
   * @private
   */
  get initDescriptor() {
    let descriptor = this._initDescriptor;
    if (!descriptor || !hasOwn(this, "_initDescriptor")) {
      this._initDescriptor = descriptor = this.makeInitter();
    }
    return descriptor;
  }
  /**
   * This method converts a value presented to the setter to the value that will be processed by config hook methods
   * (e.g., `changeFoo` and `updateFoo`).
   *
   * @param {*} value The value passed to the setter.
   * @param {Core.Base} instance The instance
   * @returns {*}
   * @internal
   */
  convert(value, instance) {
    return value;
  }
  /**
   * This method compares two values for semantic equality. By default, this is based on the `===` operator. This
   * is often overridden for configs that accept `Date` or array values.
   * @param {*} value1
   * @param {*} value2
   * @returns {Boolean}
   * @internal
   */
  equal(value1, value2) {
    return value1 === value2;
  }
  /**
   * Extends this config with a given additional set of options. These objects are just prototype extensions of this
   * instance.
   * @param {Object} options
   * @returns {Core.Config}
   * @internal
   */
  extend(options) {
    const cfg = Object.assign(Object.create(this), options), { convert, equal, merge } = options, { convertMethods, equalityMethods } = Config;
    if (typeof convert === "string") {
      if (!(cfg.convert = convertMethods[convert]) && convert.endsWith("[]")) {
        cfg.convert = convertMethods[convert] = Config.makeArrayConvert(convertMethods[convert.slice(0, -2)]);
      }
    }
    if (typeof equal === "string") {
      if (!(cfg.equal = equalityMethods[equal]) && equal.endsWith("[]")) {
        cfg.equal = equalityMethods[equal] = Config.makeArrayEquals(equalityMethods[equal.slice(0, -2)]);
      }
    }
    if (typeof merge === "string") {
      cfg.merge = Config.mergeMethods[merge];
    }
    return cfg;
  }
  /**
   * Defines the property on a given target object via `Reflect.defineProperty()`. If the object has its own getter,
   * it will be preserved. It is invalid to define a setter.
   * @param {Object} target
   * @internal
   */
  define(target) {
    const { name } = this, existing = getOwnPropertyDescriptor(target, name);
    let descriptor = this.descriptor;
    if (existing == null ? void 0 : existing.get) {
      descriptor = Object.assign({}, descriptor);
      descriptor.get = observableGet(name, existing.get);
    }
    defineProperty(target, name, descriptor);
  }
  /**
   * Defines the property initter on the `target`. This is a property getter/setter that propagates the configured
   * value when the property is read.
   * @param {Object} target
   * @param {*} value
   * @internal
   */
  defineInitter(target, value) {
    const me = this, { name } = me, properties = target[instancePropertiesSymbol];
    let lazyValues, prop;
    if (!properties[name] && /* assign */
    (prop = getOwnPropertyDescriptor(target, name)) && !("value" in prop)) {
      properties[name] = prop;
    }
    defineProperty(target, name, me.initDescriptor);
    if (me.lazy) {
      lazyValues = target[lazyConfigValues] || (target[lazyConfigValues] = /* @__PURE__ */ new Map());
      lazyValues.set(name, value);
    }
  }
  static makeArrayConvert(fn) {
    return function(value, instance) {
      if (value != null) {
        if (Array.isArray(value)) {
          value = value.map((v, i) => fn.call(this, v, instance));
        } else {
          value = [fn.call(this, value, instance)];
        }
      }
      return value;
    };
  }
  /**
   * Returns an equality function for arrays of a base type, for example `'date'`.
   * @param {Function} [fn] The function to use to compare array elements. By default, operator `===` is used.
   * @returns {Function}
   * @private
   */
  static makeArrayEquals(fn) {
    return (value1, value2) => {
      let i, equal = value1 && value2 && value1.length === (i = value2.length);
      if (equal && Array.isArray(value1) && Array.isArray(value2)) {
        if (fn) {
          while (equal && i-- > 0) {
            equal = fn(value1[i], value2[i]);
          }
        } else {
          while (equal && i-- > 0) {
            equal = value1[i] === value2[i];
          }
        }
      } else {
        equal = fn ? fn(value1, value2) : value1 === value2;
      }
      return equal;
    };
  }
  /**
   * Returns the key to use in the Config `cache`.
   * @param {String} name The name of the config property.
   * @param {Object} options The config property options.
   * @returns {String}
   * @private
   */
  static makeCacheKey(name, options) {
    const keys = Object.keys(options).sort();
    for (let key, type, value, i = keys.length; i-- > 0; ) {
      value = options[key = keys[i]];
      if (value == null && value === false) {
        keys.splice(i, 1);
      } else {
        type = typeof value;
        if (type === "function") {
          return null;
        }
        if (type === "string") {
          keys[i] = `${key}:"${value}"`;
        } else if (type === "number") {
          keys[i] = `${key}:${value}`;
        }
      }
    }
    return keys.length ? `${name}>${keys.join("|")}` : name;
  }
  /**
   * Creates and returns a property descriptor for this config suitable to be passed to `Reflect.defineProperty()`.
   * @returns {Object}
   * @private
   */
  makeDescriptor() {
    const { base, field, changer, updater, name } = this;
    if (base !== this) {
      return base.descriptor;
    }
    return {
      get() {
        var _a;
        (_a = this.configObserver) == null ? void 0 : _a.get(name, this);
        return this[field];
      },
      set(value) {
        var _a, _b;
        const me = this, origPassedValue = value, config = me.$meta.configs[name];
        let was = me[field], applied, newValue;
        if (typeof value === "string") {
          let resolvedValue = value;
          if (value.startsWith("up.")) {
            resolvedValue = (_a = me.owner) == null ? void 0 : _a.resolveProperty(value.slice(3));
          } else if (value.startsWith("this.")) {
            resolvedValue = me.resolveProperty(value.slice(5));
          }
          if (resolvedValue !== void 0 && typeof resolvedValue !== "function") {
            value = resolvedValue;
          }
        }
        if (config.convert !== defaultConvert) {
          value = config.convert(value, me);
        }
        if (me[changer]) {
          applied = (newValue = me[changer](value, was)) === void 0 && 1;
          if (!applied) {
            value = newValue;
            was = me[field];
          }
        }
        if (!applied && !(config.equal === strictEquals ? was === value : config.equal(was, value))) {
          me[field] = value;
          applied = 2;
          (_b = me[updater]) == null ? void 0 : _b.call(me, value, was);
        }
        if (applied && !me.isDestroyed && !me.onConfigChange.$nullFn) {
          me.onConfigChange({ name, value, was, config, applied, origPassedValue });
        }
      }
    };
  }
  /**
   * Creates and returns a property descriptor for this config's initter suitable to pass to
   * `Reflect.defineProperty()`.
   * @returns {Object}
   * @private
   */
  makeInitter() {
    const config = this;
    if (config !== config.base) {
      if (config.lazy) {
        return config.makeLazyInitter();
      }
      return config.base.initDescriptor;
    }
    return config.makeBasicInitter();
  }
  makeBasicInitter() {
    const config = this, { initializing, name } = config;
    return {
      configurable: true,
      get() {
        const me = this;
        config.removeInitter(me);
        me[initializing] = true;
        me[name] = me[configuringSymbol][name];
        me[initializing] = false;
        me.configDone[name] = true;
        return me[name];
      },
      set(value) {
        config.removeInitter(this);
        this.configDone[name] = true;
        this[name] = value;
      }
    };
  }
  makeLazyInitter() {
    const config = this, { initializing, name } = config;
    return {
      configurable: true,
      get() {
        const me = this, value = me[lazyConfigValues].get(name);
        config.removeInitter(me);
        if (!me.isDestroying) {
          me[initializing] = true;
          me[name] = value;
          me[initializing] = false;
        }
        return me[name];
      },
      set(value) {
        config.removeInitter(this);
        this[name] = value;
      }
    };
  }
  /**
   * Removes the property initter and restores the instance to its original form.
   * @param {Object} instance
   * @private
   */
  removeInitter(instance) {
    const { name } = this, instanceProperty = instance[instancePropertiesSymbol][name], lazyValues = instance[lazyConfigValues];
    if (instanceProperty) {
      defineProperty(instance, name, instanceProperty);
    } else {
      delete instance[name];
    }
    if ((lazyValues == null ? void 0 : lazyValues.delete(name)) && !lazyValues.size) {
      delete instance[lazyConfigValues];
    }
  }
  setDefault(cls, value) {
    defineProperty(cls.prototype, this.field, {
      configurable: true,
      writable: true,
      // or else "this._value = x" will fail
      value
    });
  }
  /**
   * This method combines (merges) two config values. This is called in two cases:
   *
   *  - When a derived class specifies the value of a config defined in a super class.
   *  - When a value is specified in the instance config object.
   *
   * @param {*} newValue In the case of derived classes, this is the config value of the derived class. In the case
   * of the instance config, this is the instance config value.
   * @param {*} currentValue In the case of derived classes, this is the config value of the super class. In the case
   * of the instance config, this is the class config value.
   * @param {Object} metaNew The class meta object from which the `newValue` is coming. This parameter is `null` if
   * the `newValue` is from an instance configuration.
   * @param {Object} metaCurrent The class meta object from which the `currentValue` is coming. This parameter is
   * `null` if the `currentValue` is not from a class configuration.
   * @returns {*}
   * @internal
   */
  merge(newValue, currentValue) {
    if (currentValue && newValue && Objects.isObject(newValue)) {
      if (currentValue.isBase) {
        return currentValue.setConfig(newValue);
      }
      if (Objects.isObject(currentValue)) {
        newValue = Objects.merge(Objects.clone(currentValue), newValue);
      }
    }
    return newValue;
  }
}
const { prototype } = Config, { convert: defaultConvert, equal: strictEquals } = prototype;
Config.symbols = {
  configuring: configuringSymbol,
  instanceProperties: instancePropertiesSymbol,
  lazyConfigs: lazyConfigValues
};
Config.cache = /* @__PURE__ */ Object.create(null);
Config.convertMethods = {
  array: Config.makeArrayConvert(defaultConvert),
  date(value, instance) {
    let ret = value, converted, format, formats;
    if (specialDates[value]) {
      ret = specialDates[value]();
    } else if (value != null) {
      if (typeof value !== "string") {
        converted = new Date(value);
      } else {
        formats = this.formats;
        if (formats) {
          formats = formats.flatMap((f) => f in instance ? instance[f] : f);
          for (format of formats) {
            converted = format && Helpers.Date.parse(value, format);
            if (converted) {
              break;
            }
          }
        }
        if (!converted) {
          converted = Helpers.Date.parse(value);
        }
      }
      if (isNaN(converted)) {
        if (this.strict) {
          throw new Error(`Invalid date: ${value}`);
        }
      } else {
        ret = converted;
      }
    }
    return ret;
  },
  day(value, instance) {
    value = Config.convertMethods.date.call(this, value, instance);
    value == null ? void 0 : value.setMinutes(0, 0, 0);
    return value;
  }
};
Config.equalityMethods = {
  array: Config.makeArrayEquals(),
  date: dateEquals,
  day: dateEquals,
  strict: Config.equal = strictEquals
};
Config.mergeMethods = {
  distinct(newValue, oldValue) {
    let ret = oldValue ? oldValue.slice() : [];
    if (newValue != null) {
      if (Objects.isObject(newValue)) {
        if (oldValue === void 0) {
          ret = newValue;
        } else {
          let key, index;
          for (key in newValue) {
            index = ret.indexOf(key);
            if (newValue[key]) {
              if (index < 0) {
                ret.push(key);
              }
            } else if (index > -1) {
              ret.splice(index, 1);
            }
          }
        }
      } else if (Array.isArray(newValue)) {
        newValue.forEach((v) => !ret.includes(v) && ret.push(v));
      } else if (!ret.includes(newValue)) {
        ret.push(newValue);
      }
    }
    return ret;
  },
  merge: Config.merge = prototype.merge,
  classList(newValue, oldValue) {
    if (typeof newValue === "string") {
      if (!newValue.length) {
        return oldValue;
      }
      newValue = newValue.split(whitespace);
    }
    if (Array.isArray(newValue)) {
      newValue = newValue.reduce(createClsProps, {});
    }
    return Config.merge(newValue, oldValue);
  },
  objects(newValue, oldValue) {
    return newValue === true ? oldValue || {} : Config.merge(newValue, oldValue);
  },
  replace(newValue) {
    return newValue;
  },
  items(newValue, oldValue, metaNew, metaCurrent) {
    if (metaCurrent) {
      return Objects.mergeItems(oldValue, newValue, {
        merge: (oldValue2, newValue2) => prototype.merge(newValue2, oldValue2)
      });
    }
    return prototype.merge(newValue, oldValue);
  }
};
Object.assign(prototype, {
  _descriptor: null,
  _initDescriptor: null,
  /**
   * A function that compares values for equality. This test is used to determine if the `update` method should be
   * called when the setter is invoked.
   *
   * To handle `Date` values:
   * ```
   *  class Foo extends Base {
   *      static get configurable() {
   *          return {
   *              date : {
   *                  $config : {
   *                      equal : 'date'
   *                  },
   *
   *                  value : null
   *              }
   *          }
   *      }
   *
   *      updateDate(date) {
   *          // date has changed
   *      }
   *  }
   * ```
   *
   * Also useful for some configs:
   * ```
   *  class Foo extends Base {
   *      static get configurable() {
   *          return {
   *              bar : {
   *                  $config : {
   *                      equal : ObjectHelper.isEqual
   *                  },
   *
   *                  value : null
   *              }
   *          }
   *      }
   *
   *      updateBar(value) {
   *          // value has changed
   *      }
   *  }
   * ```
   * @config {Function} equal
   * @internal
   */
  /**
   * Indicates that values are Date objects.
   * @config {Boolean}
   * @default
   */
  date: false,
  /**
   * Indicates that this config property should not automatically initialize during construction. When this property
   * is set to `true`, initialization is triggered by the first use of the config property's getter.
   *
   * This property can alternatively be set to a string, in which case it can be initialized as a group using the
   * {@link Core.Base#function-triggerConfigs} method which will initialize all lazy configs with the same value for
   * this property. Note: the config will still initialize on first use if that occurs prior to the call to
   * `triggerConfigs`.
   * @config {Boolean|String}
   * @default
   * @internal
   */
  lazy: false,
  /**
   * Indicates that this config property should automatically be set to `null` on destroy.
   * @config {Boolean}
   * @default
   * @internal
   */
  nullify: false,
  /**
   * Indicates that this config participates in rendering. This has does not affect the behavior of the property
   * directly, but allows classes that perform rendering to detect which config changes will affect the rendered
   * result.
   * @config {Boolean}
   * @default
   * @internal
   */
  render: false
});
Config._$name = "Config";
export {
  Config as default
};
